/**
 * Please checkout this [Coda doc](https://coda.io/d/Infrastructure_dK_rvJRCUMg/PCB-Layout_suXGB#_luDnX)
 * to understand how PcbLayout works
 *
 * Also see more recent:
 * https://coda.io/d/North-Star-Architecture-Notes_dH-26nnEKbE/Pcb-Layout-Discussion_suBvS#_luGa_
 */
import { FluxErrorCodes } from "@buildwithflux/constants";
import { getLayerIdentifier, pcbLayoutRootNodeId, PcbNodeTypes, } from "@buildwithflux/models";
import { areWeTestingWithJest } from "@buildwithflux/shared";
import { Clipper } from "../../clipper";
import { FluxBaseError } from "../../flux_error";
// eslint-disable-next-line boundaries/element-types
import { convertLegacyOrModernKiCadFootprintToFluxFootprint } from "../../imports";
import { addPcbNodes, getAncestorUidByType, getDescendants, getDescendantsOfType, getTopLevelLayouts, PcbNodeHelper, removeNodesAndDescendants, } from "../../nodes";
import { getSelectedViaConfigs, getStackupLayersAndViaConfigs, SmartViaConfigsCache } from "../../smart_via";
import { PcbAutoPositionUpdater } from "./auto_initial_position";
import { filterOrphanNodes, getTransformBakedRules, hasAbsTransformChanged, updateBakedRuleValues } from "./helpers";
import { computeEffectiveNetForNode } from "./helpers/computeEffectiveNetForNode";
import { PcbImpedanceMatcher } from "./impedance";
import { PcbNodeBaker } from "./node_baking";
import { PcbTreeManager } from "./PcbTreeManager";
import { PcbLayoutRuntimeRulesManager } from "./rule_parsing_and_compilation";
import FillVirtualNodeGenerator from "./virtual_node_generators/generators/fill/FillVirtualNodeGenerator";
import SmartViaVirtualNodeGenerator from "./virtual_node_generators/generators/smart_via/SmartViaVirtualNodeGenerator";
import PcbLayoutVirtualNodeGenerator from "./virtual_node_generators/PcbLayoutVirtualNodeGenerator";
// TODO: Once Kicad related importers are moved to core, we can then remove
// parameters `kicadFootprintToFluxFootprint` and `getKicadImporterForPcbLayout` from
// this file and all other files used by layout engine.
// See issue: https://github.com/buildwithflux/flux-app/issues/9229
/**
 * Please checkout this [Coda doc](https://coda.io/d/Infrastructure_dK_rvJRCUMg/PCB-Layout_suXGB#_luDnX)
 * to understand how PcbLayout works.
 *
 * Also see more recent:
 * https://coda.io/d/North-Star-Architecture-Notes_dH-26nnEKbE/Pcb-Layout-Discussion_suBvS#_luGa_
 *
 * As of Nov 2022, this class is designed to be run in a web worker. The public
 * methods return only nodes that changed in order to minimize data transfer,
 * except for bakeAllNodes. And when it is run in a web worker, it will mutate
 * inputs in place for performance.
 */
export class PcbLayoutEngine {
    logger;
    featureFlags;
    elements;
    nets;
    treeManager;
    pcbAutoPositioning;
    pcbLayoutVirtualNodeGenerator;
    fillVirtualNodeGenerator;
    smartViaVirtualNodeGenerator;
    runtimeRulesManager;
    pcbImpedanceMatcher;
    pcbNodeBaker;
    computeEffectiveNetForNodeCache = { allNetNodesCache: new Map() };
    constructor(documentState, treeNodes, pcbLayoutRules, logger, featureFlags) {
        this.logger = logger;
        this.featureFlags = featureFlags;
        this.elements = documentState.elements;
        this.nets = documentState.nets ?? {};
        this.treeManager = new PcbTreeManager(this.featureFlags?.enablePcbOrphanNodeRemoval ? filterOrphanNodes(treeNodes, logger) : treeNodes, logger);
        this.runtimeRulesManager = new PcbLayoutRuntimeRulesManager();
        this.pcbNodeBaker = new PcbNodeBaker(documentState, pcbLayoutRules, this.runtimeRulesManager, this.treeManager, logger);
        this.pcbAutoPositioning = new PcbAutoPositionUpdater(documentState.elements, this.treeManager, logger);
        this.pcbLayoutVirtualNodeGenerator = new PcbLayoutVirtualNodeGenerator(documentState.elements, documentState.assets, this.treeManager, this.runtimeRulesManager, logger, convertLegacyOrModernKiCadFootprintToFluxFootprint);
        this.fillVirtualNodeGenerator = new FillVirtualNodeGenerator(this.pcbNodeBaker.pcbRuleCompiler, this.treeManager, this.bakeRulesAndCalculationsToNode.bind(this), logger);
        this.smartViaVirtualNodeGenerator = new SmartViaVirtualNodeGenerator(this.treeManager);
        this.pcbImpedanceMatcher = new PcbImpedanceMatcher(this.treeManager);
    }
    /**
     * Bake the given nodes and re-run auto positioning.
     *
     * NOTE: this does NOT generate fills, unlike bakeAllNodes. The caller is
     * expected to call generateFillNodes separately.
     *
     * @see rebakeNodes also
     * @see bakeNode for inner method
     */
    async bakeNodes(nodes) {
        const virtualNodesGoodsFromRules = [];
        const addedNodes = {};
        // Generate impedance matching properties for the incomming nodes before baking.
        // We do this up front and update the properties over time as nodes are updated
        // because it is a relatively expensive operation.
        this.pcbImpedanceMatcher.mergeImpedanceMatchingProps(nodes.map((node) => node.uid), this.nets, this.elements);
        for (const node of nodes) {
            // QUESTION: Can remove this at least when updating?
            addPcbNodes(this.treeManager.treeNodes, [node]);
            // NOTE: put yieldOrContinue here because
            // bakeRulesAndCalculationsToNode is not obviously recursive... top
            // level only should be enough
            await yieldOrContinue();
            const { virtualNodesGoodsFromRules: goods, updatedNodes: updatedNodesFromBake } = await this.bakeNode(node.uid);
            if (goods) {
                virtualNodesGoodsFromRules.push(...goods);
            }
            if (updatedNodesFromBake) {
                Object.assign(addedNodes, updatedNodesFromBake);
            }
        }
        this.autoPosition(addedNodes);
        return {
            pcbLayoutNodes: addedNodes,
            virtualNodesGoodsFromRules,
            virtualFillNodes: undefined,
        };
    }
    /**
     * NOTE: this method is the slowest one in the class. It should be called
     * only when more selective methods don't work.
     *
     * @see bakeNode for inner method
     */
    async bakeAllNodes() {
        await Clipper.initialize(); // Ensure Clipper is initialized before baking nodes
        // Update the impedance matching properties for the incoming nodes
        // We do this up front and update the properties over time as nodes are updated
        // because it is a relatively expensive operation.
        this.pcbImpedanceMatcher.mergeImpedanceMatchingProps(Object.keys(this.nets), this.nets, this.elements);
        if (!this.treeManager.treeNodes[pcbLayoutRootNodeId]) {
            throw new Error("Missing root node");
        }
        const { virtualNodesGoodsFromRules } = await this.bakeNode(pcbLayoutRootNodeId);
        const pcbLayoutNodes = this.treeManager.treeNodes;
        this.autoPosition(pcbLayoutNodes);
        const virtualFillNodes = await this.generateFillNodes(undefined);
        return {
            // NOTE: this assumes all nodes have been updated
            // QUESTION: would it make sense to return only changed nodes here?
            pcbLayoutNodes,
            virtualNodesGoodsFromRules,
            virtualFillNodes,
        };
    }
    /**
     * Re-bake the given baked nodes.
     *
     * NOTE: this does NOT generate fills, unlike bakeAllNodes.
     *
     * @see bakeNodes also
     * @see rebakeNode for inner method
     */
    async rebakeNodes(nodeUids, hasInheritedRules, hasInheritedCalculations, requiresDescendantRebake, requiresNetDescendantRebake) {
        const virtualNodesGoodsFromRules = [];
        const updatedNodes = {};
        // This workaround triggers re-bake (and rerender) of layout if changes are made to a Net with Connected Layers
        // rule. This ensure nodes (like Pads) that are non-descendants of the Net, have opportunity to renderer. That's
        // nessisary for cases where connectivity between Fill and a Pad (elsewhere in the object tree) changes due to
        // Connected Layer toggle, etc, on the Net node. See this loom for further detaiL:
        // https://www.loom.com/share/40b0d487760a486f919cd06f209e80ac?sid=e72cd1c8-2d25-4656-b43a-dfe6b9029152
        nodeUids = this.addLayoutUidIfUpdateIncludesNetWithConnectedLayersRuleChange(nodeUids);
        // Update the impedance matching properties for the incoming nodes
        // We do this up front and update the properties over time as nodes are updated
        // because it is a relatively expensive operation.
        this.pcbImpedanceMatcher.mergeImpedanceMatchingProps(nodeUids, this.nets, this.elements);
        // NOTE: for loop to guarantee order
        for (const nodeUid of nodeUids) {
            const { virtualNodesGoodsFromRules: goods, updatedNodes: updatedNodesFromBake } = await this.rebakeNode(nodeUid, hasInheritedRules, hasInheritedCalculations, requiresDescendantRebake, requiresNetDescendantRebake);
            if (goods) {
                virtualNodesGoodsFromRules.push(...goods);
            }
            if (updatedNodesFromBake) {
                Object.assign(updatedNodes, updatedNodesFromBake);
            }
        }
        this.autoPosition(updatedNodes);
        return {
            virtualNodesGoodsFromRules,
            pcbLayoutNodes: this.includeAutoPositionedDescendants(updatedNodes),
            virtualFillNodes: undefined,
        };
    }
    /** Remove nodes from the document data in the worker */
    removeNodes(nodesToRemove) {
        const nodesToRemoveUids = nodesToRemove.filter((uid) => uid in this.treeManager.treeNodes);
        if (nodesToRemoveUids.length > 0) {
            removeNodesAndDescendants(this.treeManager.treeNodes, nodesToRemoveUids);
        }
    }
    /**
     * Generates virtual fill nodes (fills, vias) and updated fill shapes for
     * the given nodes. If no nodes are given, all nodes are used.
     */
    generateFillNodes(updatedNodes) {
        const resetCache = !updatedNodes;
        return this.fillVirtualNodeGenerator.generate(resetCache ? this.treeManager.treeNodes : updatedNodes, resetCache);
    }
    /**
     * Update the cache for selected via configs for smart vias to speed up the
     * process of selecting via configs at runtime.
     *
     * This method is expensive as it depends on `getSelectedViaConfigs` which is O(n^2), so call it inside the worker.
     *
     * @param cache - Smart via configs cache
     * @returns Updated cache object
     */
    updateSmartViaConfigsCache(cache) {
        const treeNodes = this.treeManager.treeNodes;
        const configsCache = new SmartViaConfigsCache(cache);
        const rootNode = treeNodes[pcbLayoutRootNodeId];
        if (!rootNode) {
            return configsCache.cache;
        }
        const stackupInfoCache = new Map();
        const smartViaNodes = getDescendantsOfType(treeNodes, rootNode, PcbNodeTypes.smartVia, [], false);
        for (const smartViaNode of smartViaNodes) {
            // TODO: why are bakedRules sometimes not defined here?
            const bakedRules = smartViaNode.bakedRules;
            if (!bakedRules) {
                continue;
            }
            const { connectedLayers, viaOptions, selectedViaConfigs } = bakedRules;
            const { copperLayers, viaConfigs } = getStackupLayersAndViaConfigs(bakedRules, this.treeManager.treeNodes, stackupInfoCache);
            configsCache.copperLayers = copperLayers;
            if (!configsCache.has(connectedLayers, viaOptions)) {
                configsCache.set(connectedLayers, viaOptions, selectedViaConfigs.map((config) => config.uid));
            }
            const unconnectedLayers = copperLayers
                .map(getLayerIdentifier)
                .filter((layer) => !connectedLayers.includes(layer));
            for (const unconnectedLayer of unconnectedLayers) {
                const newConnectedLayers = [...connectedLayers, unconnectedLayer];
                if (!configsCache.has(newConnectedLayers, viaOptions)) {
                    const newSelectedViaConfigs = getSelectedViaConfigs(viaOptions, newConnectedLayers, viaConfigs, copperLayers);
                    configsCache.set(newConnectedLayers, viaOptions, newSelectedViaConfigs.map((config) => config.uid));
                }
            }
        }
        return configsCache.cache;
    }
    /**
     * This workaround enables re-bake (or rerender) of nodes that undergo change of connectivity to Fill geometry
     * due to interactions like; "connected layer rule toggle, on a Net node with Fill, that's on the same connected
     * layer as a Pad elsewhere in the object tree". Obtaining the ancestor Layout allows a full re-render of the PCB,
     * ensuring and connected descendant nodes have opportunity to update their keepout/connectivity with the fill
     */
    addLayoutUidIfUpdateIncludesNetWithConnectedLayersRuleChange(updatedUids) {
        for (const nodeUid of updatedUids) {
            const node = this.treeManager.treeNodes[nodeUid];
            if (!node) {
                continue;
            }
            if (node.type === PcbNodeTypes.net && node.pcbNodeRuleSet.connectedLayers) {
                const ancestorLayoutUid = getAncestorUidByType(this.treeManager.treeNodes, nodeUid, PcbNodeTypes.layout);
                if (!ancestorLayoutUid) {
                    continue;
                }
                return updatedUids.concat(ancestorLayoutUid);
            }
        }
        return updatedUids;
    }
    /**
     * This is the most important function in PcbLayoutEngine. It
     * recursive bakes all child nodes of nodeUid.
     *
     * @see rebakeNode also
     */
    async bakeNode(nodeUid) {
        const node = this.treeManager.treeNodes[nodeUid];
        if (!node) {
            // HACK: Node shouldn't be undefined here, but sometimes the cache isn't synced after
            // a node is deleted. We should fix that, but it runs deep; out-of-scope for now.
            if (!areWeTestingWithJest()) {
                // HACK: too noisy in jest tests
                this.logger.captureError(new PcbLayoutEngineNodeDataIntegrityError(`Node ${nodeUid} no longer exists. This is probably because the cached node index wasn't updated immediately after deleting a node`));
            }
            return { virtualNodesGoodsFromRules: undefined, updatedNodes: undefined };
        }
        const virtualNodesGoodsFromRules = [];
        const updatedNodes = {};
        const { goods, updatedNode } = this.bakeRulesAndCalculationsToNode(node, false);
        if (goods) {
            virtualNodesGoodsFromRules.push(goods);
        }
        if (updatedNode) {
            updatedNodes[updatedNode.uid] = updatedNode;
        }
        // NOTE: cached index will reset on every layout or footprint node, as
        // renderTreeNodes grows. So time complexity of this recursive function
        // is worst case: total number of nodes * number of elements. Using our
        // average of 100 nodes per element, we get total iterations: 100 *
        // total number of nodes, or 2M for a 200 element doc.
        // NOTE: Need to create a new copy of array here because we might
        // mutate the childUids during baking - that's why this spread is required
        const childrenUids = [...(this.treeManager.treeNodes[nodeUid]?.childUids ?? [])];
        for (const childUid of childrenUids) {
            await yieldOrContinue();
            const { virtualNodesGoodsFromRules: goodsFromChildren, updatedNodes: updatedNodesFromChildren } = await this.bakeNode(childUid);
            if (goodsFromChildren) {
                virtualNodesGoodsFromRules.push(...goodsFromChildren);
            }
            if (updatedNodes) {
                Object.assign(updatedNodes, updatedNodesFromChildren);
            }
        }
        return { virtualNodesGoodsFromRules, updatedNodes };
    }
    /**
     * This is the second most important function in PcbLayoutEngine. It
     * recursive bakes all child nodes of nodeUid, assuming the nodes are
     * already baked for optimized performance.
     *
     * @see bakeNode also
     */
    async rebakeNode(nodeUid, hasInheritedRules, hasInheritedCalculations, requiresDescendantRebake, requiresNetDescendantRebake, _skipRebake = false) {
        const nodeData = this.treeManager.treeNodes[nodeUid];
        /**
         * HACK: The whole layout process is normally top-down only, meaning
         * only the input node and its descendents are baked. However, this
         * special case block jumps from the input node to its footprint
         * ancestor. The reason we should do this that the asset rule of the
         * footprint may need to be reapplied to setup `runtimePcbLayoutRules`.
         *
         * For example:
         * 1. Updating a pad under a part footprint, for example, updating its
         *    shape. Without rebaking from the footprint, the pad will lose its
         *    position.
         * 2. Disabling mounting holes by disabling the mounts container with a
         *    global rule. Without rebaking from the footprint, the holes will
         *    not disappear.
         */
        const parentFootprintUid = getAncestorUidByType(this.treeManager.treeNodes, nodeUid, PcbNodeTypes.footprint);
        if (parentFootprintUid &&
            // HACK: these conditions all depend on how
            // FootprintVirtualNodeGenerator always re-creates a rule for every
            // pad node on every run of of PcbLayoutEngine.
            nodeData.type === PcbNodeTypes.pad &&
            !this.runtimeRulesManager.runtimePcbLayoutRules[nodeUid] &&
            // If skipRebake, we wont bake this node at all so we dont need
            // to bake its parentFootprintNode either Otherwise, this will
            // re-generate footprint virtual nodes and rebake them
            !_skipRebake) {
            return await this.bakeNode(parentFootprintUid);
        }
        const virtualNodesGoodsFromRules = [];
        const updatedNodes = {};
        // First bake current node
        const { goods, updatedNode } = this.bakeRulesAndCalculationsToNode(nodeData, _skipRebake);
        if (goods) {
            virtualNodesGoodsFromRules.push(goods);
        }
        if (updatedNode) {
            updatedNodes[updatedNode.uid] = updatedNode;
        }
        // Then bake all children nodes if the rule should be inherited
        const childrenUids = this.treeManager.treeNodes[nodeUid]?.childUids ?? [];
        // NOTE: performance optimization. Otherwise, it would
        // mean that changes to position and rotation on a parent would
        // trigger an entire rebake of that subtree. In the past that's made
        // it slow to move/rotate things and I suspect that's still likely
        // to be the case.
        const skipRebakeForChildren = hasInheritedCalculations &&
            !hasInheritedRules &&
            !requiresDescendantRebake &&
            !requiresNetDescendantRebake &&
            !goods?.shouldRebakeChildren;
        const childrenUidsToBake = new Set();
        // Only re-bake children nodes if there are inherited rules or calculations or virtual nodes
        // See allChildNodesWillInheritThisRule and affectsDescendantCalculations definitions.
        if (hasInheritedRules || hasInheritedCalculations || requiresDescendantRebake || goods?.shouldRebakeChildren) {
            for (const childUid of childrenUids) {
                childrenUidsToBake.add(childUid);
            }
        }
        // Some rules (such as keepouts) are inherited from a net node to all children AND net belonging nodes
        // (for example, pads that are part of that net, which are out of the net tree)
        if (requiresNetDescendantRebake) {
            // All children of the net node are part of it
            for (const childUid of childrenUids) {
                childrenUidsToBake.add(childUid);
            }
            // All other out of tree nodes, we need to iterate over all the nodes
            const rootNode = this.treeManager.treeNodes[pcbLayoutRootNodeId];
            if (!rootNode) {
                throw new Error("Missing root node");
            }
            const descendants = getDescendants(this.treeManager.treeNodes, rootNode)
                // Don't rebake the nets to prevent a circular rebake (which is impossible in the current system anyway)
                .filter((n) => n.type !== PcbNodeTypes.net);
            for (const node of descendants) {
                const nodeNetUid = computeEffectiveNetForNode(node, this.treeManager.treeNodes, this.nets, this.computeEffectiveNetForNodeCache);
                if (nodeNetUid === nodeUid) {
                    childrenUidsToBake.add(node.uid);
                }
            }
        }
        for (const childUid of childrenUidsToBake) {
            await yieldOrContinue();
            const { virtualNodesGoodsFromRules: goods, updatedNodes: updatedNodesFromBake } = await this.rebakeNode(childUid, hasInheritedRules, hasInheritedCalculations, requiresDescendantRebake, requiresNetDescendantRebake, skipRebakeForChildren);
            if (goods) {
                virtualNodesGoodsFromRules.push(...goods);
            }
            Object.assign(updatedNodes, updatedNodesFromBake);
        }
        // There used to be code here to return the # of nodes being updated in ordrer to check perf budget.
        // Now we are returning virtual nodes being generated/removed, and we will
        // tackle perf budget later
        return { virtualNodesGoodsFromRules, updatedNodes };
    }
    bakeRulesAndCalculationsToNode(nodeData, skipRebake = false) {
        let newlyBakedRules;
        let flattenedRules;
        const parentNode = this.treeManager.treeNodes[nodeData.parentUid];
        let impedanceMatchedNode = nodeData;
        if (!skipRebake) {
            // We need to generate the impedance matching fields before we bake the rules because
            // the impedance matching fields are used to for generating global rules that are used
            // to link the impedance pairs together
            impedanceMatchedNode = this.pcbImpedanceMatcher.addImpedanceMatchingFieldsForNode(nodeData);
            const rules = this.pcbNodeBaker.bakeNode(impedanceMatchedNode);
            flattenedRules = rules.flattenedRules;
            newlyBakedRules = rules.bakedRules;
        }
        const bakedRules = newlyBakedRules ?? impedanceMatchedNode.bakedRules;
        // TODO: is this !bakedRules case needed? Typing here seems to indicate bakedRules is always defined
        if (!bakedRules) {
            return { goods: undefined, updatedNode: undefined };
        }
        let virtualNodesGoodsFromRules;
        // Generate virtual nodes somewhat separately from the actual rules baking. This
        // keeps the rules baking more pure-functional and easier to reason about.
        /**
         * NOTE: `generateVirtualNodesFromRules` returns NEW unbaked virtual
         * nodes (see typing). MOREOVER, it _also mutates the virtual nodes
         * state in memory (for example, in web worker)_. In other words this
         * call will erase all existing bakedRules for the virtual nodes and
         * then rely on subsequent recursion to call rebakeNode against those
         * new virtual nodes. Hence, it would be safer to avoid calling this if
         * footprint is not changed!! Otherwise its hard to tell whether or not
         * the new virtual nodes are baked properly (for example, if
         * `skipRebake` is set, they wont be baked!)
         *
         * TODO: FLUX-4976 When refactoring this process, let's make this process atomic.
         * In other words, `generateVirtualNodesFromRules` should _generate and
         * bake_ the virtual nodes. Internal implementation can have separated
         * stages to show the transition from unbaked virtual nodes -> baked
         * virtual nodes, but caller should never worry about that.
         */
        if (newlyBakedRules && impedanceMatchedNode.type === PcbNodeTypes.footprint) {
            virtualNodesGoodsFromRules = this.pcbLayoutVirtualNodeGenerator.generateVirtualNodesFromRules(
            // TODO: fix as casting here
            impedanceMatchedNode, newlyBakedRules);
        }
        if (newlyBakedRules && impedanceMatchedNode.type === PcbNodeTypes.smartVia) {
            virtualNodesGoodsFromRules = this.smartViaVirtualNodeGenerator.generate(impedanceMatchedNode, newlyBakedRules);
            // We want to re-bake the virtual children of a smart via so their
            // positions are up-to-date when the rotation or children themselves change
            virtualNodesGoodsFromRules.shouldRebakeChildren = true;
        }
        // Check abs position/rotation separately from the baking process, so we don't need
        // to re-bake the entire subtree when only position/rotation change.
        const topLevelLayoutUid = getAncestorUidByType(this.treeManager.treeNodes, impedanceMatchedNode.uid, PcbNodeTypes.layout, true);
        const topLevelFootprintUid = getAncestorUidByType(this.treeManager.treeNodes, impedanceMatchedNode.uid, PcbNodeTypes.footprint, true);
        const rootFootprintOrLayoutUid = topLevelLayoutUid ?? topLevelFootprintUid;
        const parentLayout = rootFootprintOrLayoutUid ? this.treeManager.treeNodes[rootFootprintOrLayoutUid] : null;
        const absoluteTransforms = getTransformBakedRules(bakedRules, parentNode, parentLayout);
        // Only need to update node if either the rules or the precalculated
        // absolute rotation/position has changed.
        let updatedNode = undefined;
        if (hasAbsTransformChanged(absoluteTransforms, impedanceMatchedNode) || newlyBakedRules) {
            updatedNode = updateBakedRuleValues(impedanceMatchedNode, { ...bakedRules, ...absoluteTransforms }, this.treeManager);
            this.treeManager.updatePcbLayoutNode(impedanceMatchedNode.uid, (nodeDataDraft) => {
                if (flattenedRules) {
                    nodeDataDraft.flattenedRules = flattenedRules;
                }
            });
        }
        return { goods: virtualNodesGoodsFromRules, updatedNode };
    }
    /**
     * Auto-positions all layouts when there are updated "auto" nodes, or
     * auto-positions within any changed layout node.
     *
     * NOTE: this method should only be called after baking.
     */
    autoPosition(updatedNodes) {
        const hasAutoPosititionedNodes = Object.values(updatedNodes).some(PcbNodeHelper.isAutoPositioned);
        this.pcbAutoPositioning.autoPositionFromTop(getTopLevelLayouts(hasAutoPosititionedNodes ? this.treeManager.treeNodes : updatedNodes));
    }
    /**
     * Include all descendants an auto-positioned node. Used to ensure decedents of an auto-positioned node
     * are baked. Mutates the `bakedNodes` map to include any descendants of a auto positioned node.
     */
    includeAutoPositionedDescendants(bakedNodes) {
        for (const bakedNode of Object.values(bakedNodes)) {
            if (!PcbNodeHelper.isAutoPositioned(bakedNode)) {
                continue;
            }
            const descendants = getDescendants(this.treeManager.treeNodes, bakedNode);
            for (const descendant of descendants) {
                if (!PcbNodeHelper.isBakedNode(descendant)) {
                    continue;
                }
                bakedNodes[descendant.uid] = descendant;
            }
        }
        return bakedNodes;
    }
}
async function yieldOrContinue() {
    // NOTE: main-thread-scheduling does not run when in a web worker context
    if (!areWeTestingWithJest()) {
        // HACK: skip this for jest tests because of the following error. My
        // first attempt to fix it in jest.config.js did not work. For what
        // it's worth, the yieldOrContinue should not have any functional
        // effect, only a perf effect.
        //
        // "Jest encountered an unexpected token This usually means that you
        // are trying to import a file which Jest cannot parse, e.g. it's
        // not plain JavaScript."
        // "SyntaxError: Cannot use import statement outside a module"
        //
        // TODO: fix the issue above
        const { yieldOrContinue } = await import("main-thread-scheduling");
        await yieldOrContinue("user-visible");
    }
}
class PcbLayoutEngineNodeDataIntegrityError extends FluxBaseError {
    constructor(message, options = {}) {
        super(FluxErrorCodes.PcbLayoutNodeDataIntegrityError, message, options);
    }
}
//# sourceMappingURL=PcbLayoutEngine.js.map